import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { Feedback } from "@allaria-tech/matter/Feedback";
import type { FeedbackType } from "@allaria-tech/matter/Feedback";
import { Typography } from "@allaria-tech/matter/Typography";

import PartnerConsentBanner from "./PartnerConsentBanner";
import { PartnerConsentFlow } from "../../utils/partnerConsent";

import styles from "./PartnerConsentPage.module.scss";

interface Props {
  flow: PartnerConsentFlow;
  /** Drives where the action goes; see `PartnerConsentPage` for the source. */
  isMobile: boolean;
  /** Picks the card's icon and accent from the design system's own set. */
  type: FeedbackType;
  title: string;
  /** One entry per line; the first one is the card's own description. */
  description: string[];
  /** Countdown line below the description. */
  countdown?: string | false | 0;
  buttonText?: string;
  onButtonClick?: () => void;
}

/**
 * Terminal states: success, already accepted, and the errors. Matter's
 * `Feedback` carries the icon, title and spacing; this owns the lockup above
 * and, on mobile, the pinned action bar the card drops.
 */
const PartnerConsentFeedback = ({
  flow,
  isMobile,
  type,
  title,
  description,
  countdown,
  buttonText,
  onButtonClick,
}: Props) => {
  const {
    screen,
    screenCentered,
    feedbackWrapper,
    feedbackPanel,
    feedbackExtra,
    extraLine,
    countdown: countdownStyle,
  } = styles;

  // The card takes one description; the rest and the countdown go below it.
  const [lead, ...rest] = description.filter(Boolean);

  const action = buttonText
    ? { text: buttonText, onClick: onButtonClick }
    : undefined;

  return (
    <>
      <div className={`${screen} ${screenCentered}`}>
        <PartnerConsentBanner flow={flow} />
        <div className={feedbackWrapper}>
          <Feedback
            inline
            className={feedbackPanel}
            type={type}
            title={title}
            description={lead}
            // The inline card has no footer on mobile, so the action moves down.
            primaryAction={isMobile ? undefined : action}
          >
            {(rest.length > 0 || countdown) && (
              <div className={feedbackExtra}>
                {rest.map((line) => (
                  <Typography
                    key={line}
                    type="BODY"
                    size="S"
                    className={extraLine}
                  >
                    {line}
                  </Typography>
                ))}
                {countdown && (
                  <Typography type="BODY" size="S" className={countdownStyle}>
                    {countdown}
                  </Typography>
                )}
              </div>
            )}
          </Feedback>
        </div>
      </div>

      {isMobile && action && (
        <ButtonGroup
          container
          primary={{ ...action, type: "button", testId: "consent-feedback" }}
        />
      )}
    </>
  );
};

export default PartnerConsentFeedback;
